import type { Map as ImmutableMap } from 'immutable';
import { type ComponentType, PureComponent, type ReactNode } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import type { ButtonAccent, ButtonVariant } from 'components/Button/Button';
import { Dialog } from 'components/Dialog/Dialog';
import { closeConfirmDialog, toggleConfirmDialog } from './actions';
import { selectorConfirmDialogDisplay } from './selectors';

interface StateProps {
  showDialog: ImmutableMap<string, boolean>;
}

interface DispatchProps {
  onClose: () => void;
  onToggle: () => void;
}

export type ConfirmDialogMessages = {
  btnConfirm: string;
  btnConfirmDataTestId?: string;
  btnSecondaryConfirm?: string;
  btnSecondaryConfirmDataTestId?: string;
  header: ReactNode;
  subHeader?: ReactNode;
};

export interface ConfirmDialogOwnProps {
  /** @deprecated use children instead */
  bodyComponent?: ReactNode;
  capToWindowHeight?: boolean;
  children?: ReactNode;
  closable?: boolean;
  confirmDisabled?: boolean;
  /**
   * If not present, dialog is always shown when rendered.
   *
   * @deprecated That is actually preferred since we want to get rid of Redux on the go.
   */
  id?: string;
  keepOnConfirm?: boolean;
  msg: ConfirmDialogMessages;
  onClickConfirm: () => void;
  onClickSecondaryConfirm?: () => void;
  onCloseClick?: () => void;
  btnConfirmAccent?: ButtonAccent;
  btnConfirmDataTestId?: string;
  btnConfirmVariant?: ButtonVariant;
  btnCancelAccent?: ButtonAccent;
  btnCancelDataTestId?: string;
  btnCancelVariant?: ButtonVariant;
}

type Props = StateProps & DispatchProps & ConfirmDialogOwnProps;

class BareConfirmDialog extends PureComponent<Props> {
  componentWillUnmount() {
    const { showDialog, id, onClose, onCloseClick } = this.props;
    if (id === undefined || showDialog.get(id)) {
      onClose();
      onCloseClick?.();
    }
  }

  handleClose = () => {
    const { onToggle, onCloseClick, closable = true } = this.props;
    if (!closable) {
      return;
    }
    onToggle();
    onCloseClick?.();
  };

  handleConfirmButtonClick = () => {
    const { onClose, onClickConfirm, keepOnConfirm = false } = this.props;
    if (!keepOnConfirm) {
      onClose();
    }
    onClickConfirm();
  };

  handleSecondaryConfirmButtonClick = () => {
    const {
      onClose,
      onClickSecondaryConfirm,
      keepOnConfirm = false,
    } = this.props;
    if (!keepOnConfirm) {
      onClose();
    }
    onClickSecondaryConfirm?.();
  };

  render() {
    const {
      capToWindowHeight,
      closable = true,
      btnConfirmAccent,
      btnConfirmDataTestId,
      btnConfirmVariant = 'secondary',
      btnCancelVariant = 'primary',
      btnCancelAccent,
      btnCancelDataTestId,
      bodyComponent,
      children,
      confirmDisabled,
      id,
      msg,
      showDialog,
    } = this.props;
    return id === undefined || showDialog.get(id) ? (
      <Dialog
        capToWindowHeight={capToWindowHeight}
        closable={closable}
        headerMsg={msg.header}
        onClose={this.handleClose}
        onPrimaryClick={this.handleConfirmButtonClick}
        onSecondaryClick={this.handleSecondaryConfirmButtonClick}
        primaryBtnAccent={btnConfirmAccent}
        primaryBtnDataTestId={btnConfirmDataTestId}
        primaryBtnMsg={msg.btnConfirm}
        primaryBtnVariant={btnConfirmVariant}
        primaryDisabled={confirmDisabled}
        secondaryBtnAccent={btnCancelAccent}
        secondaryBtnDataTestId={btnCancelDataTestId}
        secondaryBtnMsg={msg.btnSecondaryConfirm}
        secondaryBtnVariant={btnCancelVariant}
        subHeaderMsg={msg.subHeader}
      >
        {children ?? bodyComponent ?? null}
      </Dialog>
    ) : null;
  }
}

export const ConfirmDialog: ComponentType<ConfirmDialogOwnProps> = connect<
  StateProps,
  DispatchProps,
  ConfirmDialogOwnProps
>(
  createStructuredSelector({
    showDialog: selectorConfirmDialogDisplay(),
  }),
  (dispatch, props) => ({
    onToggle: () => dispatch(toggleConfirmDialog(props.id)),
    onClose: () => dispatch(closeConfirmDialog(props.id)),
  }),
)(BareConfirmDialog);
